import { faBox, faFolder } from "@fortawesome/pro-regular-svg-icons";

import { Flow } from "src/api/flowTypes";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { FlowNavSidebar } from "src/flow/FlowNavSidebar";
import { useFolders } from "src/flowsOverview/v2/folderQueries";
import { DashboardContent } from "src/layout/DashboardContent";
import { PageHeader } from "src/layout/PageHeader";
import { MainSideBar } from "src/mainPage/MainSideBar";
import {
  FlowPageParams,
  getFlowVersionsUrl,
  getUrlToWsDashboard,
  WsDashboardPageCode,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const FolderNameLabel: React.FC<{
  folderName: string | undefined;
  isLoading: boolean;
}> = ({ folderName, isLoading }) => {
  if (isLoading) {
    return <Skeleton height="h-4" width="w-24" />;
  }
  return (
    <span className="flex items-center gap-x-1.5">
      <Icon icon={folderName ? faFolder : faBox} size="xs" variant="active" />
      {folderName ?? "Decision Flows"}
    </span>
  );
};

type FlowPageLayoutProps = {
  flow: Flow | undefined;
  headerActions?: React.ReactNode;
  rightPanel?: React.ReactNode;
  children: React.ReactNode;
};

export const FlowPageLayout: React.FC<FlowPageLayoutProps> = ({
  flow,
  headerActions,
  rightPanel,
  children,
}) => {
  const { flow_id, wsId, orgId } = useParamsDecode<FlowPageParams>();

  const isFlowLoading = flow === undefined;
  const folderId = flow?.flow_folder_id;
  const { data: folders, isLoading: isFoldersLoading } = useFolders(
    { workspaceId: wsId },
    { enabled: !isFlowLoading },
  );
  const folder = folders?.folders.find((f) => f.id === folderId);

  return (
    <DashboardContent sidebar={<MainSideBar />} removePadding>
      <div className="flex max-h-screen min-h-0 max-w-full grow justify-stretch">
        <div className="flex max-h-screen min-h-0 max-w-full grow flex-col justify-stretch">
          <PageHeader
            breadcrumbs={[
              {
                key: "flows-page",
                breadcrumb: (
                  <PageHeader.Breadcrumb
                    dataLoc="flows-page"
                    href={getUrlToWsDashboard({
                      orgId,
                      wsId,
                      page: WsDashboardPageCode.Flows,
                      folderId,
                    })}
                    label={
                      <FolderNameLabel
                        folderName={folder?.name}
                        isLoading={isFoldersLoading || isFlowLoading}
                      />
                    }
                    hideIcon
                  />
                ),
              },
              {
                key: "flow-name",
                breadcrumb: (
                  <PageHeader.Breadcrumb
                    dataLoc="flow-name"
                    href={getFlowVersionsUrl(orgId, wsId, flow?.id ?? flow_id)}
                    label={flow?.name ?? ""}
                    active
                  />
                ),
              },
            ]}
            sideContent={headerActions}
          />
          <DashboardContent sidebar={<FlowNavSidebar flow={flow} />}>
            {children}
          </DashboardContent>
        </div>
        {rightPanel}
      </div>
    </DashboardContent>
  );
};
